import moment from "moment-timezone";

// Set default timezone to GMT
moment.tz.setDefault("GMT");

/**
 * Convert any date to GMT format for server communication
 * @param {Date|string|moment} date - The date to convert
 * @returns {string} - ISO string in GMT timezone
 */
export const toGMT = (date) => {
  if (!date) return null;

  // If it's already a moment object, convert to GMT
  if (moment.isMoment(date)) {
    return date.tz("GMT").format();
  }

  // If it's a string or Date object, parse and convert to GMT
  return moment(date).tz("GMT").format();
};

/**
 * Convert GMT date from server to local timezone for display
 * @param {string} gmtDate - Date string from server (assumed to be in GMT)
 * @param {string} format - Display format (default: "MM/DD/YYYY hh:mm a")
 * @returns {string} - Formatted date in client's local timezone
 */
export const fromGMT = (gmtDate, format = "MM/DD/YYYY hh:mm a") => {
  if (!gmtDate) return "";

  // Parse the GMT date and convert to client's local timezone for display
  return moment.tz(gmtDate, "GMT").local().format(format);
};

/**
 * Get current date in GMT format for server communication
 * @param {string} format - Optional format (default: ISO string)
 * @returns {string} - Current date in GMT
 */
export const getCurrentGMT = (format = null) => {
  const now = moment().tz("GMT");
  return format ? now.format(format) : now.format();
};

/**
 * Get current date in client's local timezone for display
 * @param {string} format - Optional format (default: ISO string)
 * @returns {string} - Current date in client's local timezone
 */
export const getCurrentLocal = (format = null) => {
  const now = moment();
  return format ? now.format(format) : now.format();
};

/**
 * Format date for display with timezone indicator
 * @param {string} gmtDate - Date from server (in GMT)
 * @param {string} format - Display format
 * @param {boolean} showTimezone - Whether to show timezone indicator
 * @returns {string} - Formatted date in client's local timezone with optional timezone indicator
 */
export const formatForDisplay = (
  gmtDate,
  format = "MM/DD/YYYY hh:mm a",
  showTimezone = true
) => {
  if (!gmtDate) return "";

  // Convert GMT date to client's local timezone for display
  const localDate = moment.tz(gmtDate, "GMT").local();
  const formattedDate = localDate.format(format);

  if (showTimezone) {
    return `${formattedDate}`;
  }

  return formattedDate;
};

/**
 * Get timezone information for display
 * Uses user's local timezone (moment.tz.setDefault("GMT") would make moment() return GMT,
 * so we explicitly create a moment in the guessed local timezone).
 * @returns {object} - Timezone information
 */
export const getTimezoneInfo = () => {
  const localTz = moment.tz.guess();
  const now = moment.tz(new Date(), localTz);
  const gmtOffset = now.format("Z");
  const timezoneName = now.format("z");

  return {
    local: localTz,
    offset: gmtOffset,
    name: timezoneName,
    isDST: now.isDST(),
  };
};

/**
 * Convert local date to GMT for form submission
 * @param {Date|string} localDate - Local date from client
 * @returns {string} - GMT ISO string for server
 */
export const localToGMT = (localDate) => {
  if (!localDate) return null;
  return moment(localDate).tz("GMT").format();
};

/**
 * Convert GMT date to local Date object for client-side processing
 * @param {string} gmtDate - Date from server (in GMT)
 * @returns {Date} - Date object in client's local timezone
 */
export const gmtToLocalDate = (gmtDate) => {
  if (!gmtDate) return null;
  return moment.tz(gmtDate, "GMT").local().toDate();
};

/**
 * Get relative time from GMT date (in client's timezone)
 * @param {string} gmtDate - Date from server (in GMT)
 * @returns {string} - Relative time (e.g., "2 hours ago") in client's timezone
 */
export const getRelativeTime = (gmtDate) => {
  if (!gmtDate) return "";
  return moment.tz(gmtDate, "GMT").fromNow();
};

/**
 * Check if a date is in the past (using client's timezone)
 * @param {string} gmtDate - Date from server (in GMT)
 * @returns {boolean} - True if date is in the past
 */
export const isPast = (gmtDate) => {
  if (!gmtDate) return false;
  return moment.tz(gmtDate, "GMT").isBefore(moment());
};

/**
 * Check if a date is in the future (using client's timezone)
 * @param {string} gmtDate - Date from server (in GMT)
 * @returns {boolean} - True if date is in the future
 */
export const isFuture = (gmtDate) => {
  if (!gmtDate) return false;
  return moment.tz(gmtDate, "GMT").isAfter(moment());
};

/**
 * Get days until expiry (using client's timezone)
 * @param {string} gmtDate - Expiry date from server (in GMT)
 * @returns {number} - Days until expiry (negative if expired)
 */
export const getDaysUntilExpiry = (gmtDate) => {
  if (!gmtDate) return 0;
  const expiry = moment.tz(gmtDate, "GMT");
  const now = moment();
  return expiry.diff(now, "days");
};

/**
 * Format date for different contexts (in client's timezone)
 * @param {string} gmtDate - Date from server (in GMT)
 * @param {string} context - Context for formatting ('short', 'long', 'time', 'date')
 * @returns {string} - Formatted date in client's local timezone
 */
export const formatByContext = (gmtDate, context = "default") => {
  if (!gmtDate) return "";

  const formats = {
    short: "MM/DD/YY",
    long: "MMMM DD, YYYY",
    time: "hh:mm a",
    date: "MM/DD/YYYY",
    datetime: "MM/DD/YYYY hh:mm a",
    default: "MM/DD/YYYY hh:mm a",
  };

  const format = formats[context] || formats.default;
  return formatForDisplay(gmtDate, format, false);
};

/**
 * Get client's current timezone name
 * @returns {string} - Client's timezone name (e.g., "America/New_York")
 */
export const getClientTimezone = () => {
  return moment.tz.guess();
};

/**
 * Get client's timezone offset from GMT
 * @returns {string} - Timezone offset (e.g., "-05:00")
 */
export const getClientTimezoneOffset = () => {
  return moment().format("Z");
};

/**
 * Check if client is in daylight saving time
 * @returns {boolean} - True if client is in DST
 */
export const isClientInDST = () => {
  return moment().isDST();
};
